import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Spinner,
  Table,
  Td,
  Th,
} from '@/components';
import { apiJson, getApiErrorMessage } from '@/lib/api';

const HERO_CAROUSEL_MAX = 10;

type FeaturedResource = {
  id: number;
  resource_key: string;
  sort_order: number;
  is_active: boolean;
  hero_image_cdn_key: string | null;
  hero_image_url: string | null;
  title_override: string | null;
  created_at: string;
  updated_at: string;
};

type CatalogItem = {
  resource_key: string;
  title: string;
  category: string;
  source: string;
};

type CatalogResponse = { items: CatalogItem[] };

type SuggestionItem = {
  resource_key: string;
  title: string;
  category: string;
  reason: 'new' | 'popular';
  already_featured: boolean;
  published_at?: string | null;
  distinct_installations?: number | null;
  tap_count?: number | null;
};

type SuggestionsResponse = { items: SuggestionItem[] };

type BulkCreateResponse = {
  created: FeaturedResource[];
  skipped: string[];
};

/** Matches mobile HeroResourceCard mini-preview types (custom upload does not replace these). */
function usesBuiltInMiniPreview(resourceKey: string): boolean {
  const key = resourceKey;
  return (
    key.includes('flashcards') ||
    key.includes('cheatsheets') ||
    key.includes('game-build-sentence') ||
    key.includes('/(songs)/') ||
    key.includes('game-snake') ||
    key.includes('game-flappy') ||
    key.includes('game-hmong-village') ||
    key.includes('game-match-') ||
    key.includes('game-spell-') ||
    key.includes('match-the-body-parts') ||
    key.includes('game-animal-guesser') ||
    key.includes('/(diagrams)/body-parts')
  );
}

export const Route = createFileRoute('/admin/featured-resources')({
  component: FeaturedResourcesPage,
});

function FeaturedResourcesPage() {
  const qc = useQueryClient();
  const [catalogQuery, setCatalogQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [titleOverride, setTitleOverride] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['admin', 'featured-resources'],
    queryFn: () => apiJson<FeaturedResource[]>('/v1/admin/featured-resources'),
  });

  const catalogQueryResult = useQuery({
    queryKey: ['admin', 'featured-resources', 'catalog'],
    queryFn: () =>
      apiJson<CatalogResponse>('/v1/admin/featured-resources/catalog'),
  });

  const newSuggestionsQuery = useQuery({
    queryKey: ['admin', 'featured-resources', 'suggestions', 'new'],
    queryFn: () =>
      apiJson<SuggestionsResponse>(
        '/v1/admin/featured-resources/suggestions?kind=new&limit=25'
      ),
  });

  const popularSuggestionsQuery = useQuery({
    queryKey: ['admin', 'featured-resources', 'suggestions', 'popular'],
    queryFn: () =>
      apiJson<SuggestionsResponse>(
        '/v1/admin/featured-resources/suggestions?kind=popular&limit=25'
      ),
  });

  const catalogByKey = useMemo(() => {
    const map = new Map<string, CatalogItem>();
    for (const item of catalogQueryResult.data?.items ?? []) {
      map.set(item.resource_key, item);
    }
    return map;
  }, [catalogQueryResult.data]);

  const featuredKeys = useMemo(
    () => new Set((listQuery.data ?? []).map((row) => row.resource_key)),
    [listQuery.data]
  );

  const activeCount = useMemo(
    () => (listQuery.data ?? []).filter((row) => row.is_active).length,
    [listQuery.data]
  );

  const filteredCatalog = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    const items = catalogQueryResult.data?.items ?? [];
    return items
      .filter((item) => !featuredKeys.has(item.resource_key))
      .filter((item) => {
        if (!q) return true;
        return (
          item.title.toLowerCase().includes(q) ||
          item.resource_key.toLowerCase().includes(q) ||
          item.category.toLowerCase().includes(q)
        );
      })
      .slice(0, 40);
  }, [catalogQuery, catalogQueryResult.data, featuredKeys]);

  function invalidateAll() {
    void qc.invalidateQueries({ queryKey: ['admin', 'featured-resources'] });
  }

  const createMutation = useMutation({
    mutationFn: (body: {
      resource_key: string;
      title_override?: string | null;
      hero_image_cdn_key?: string | null;
    }) =>
      apiJson<FeaturedResource>('/v1/admin/featured-resources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setSelectedKey('');
      setTitleOverride('');
      setMessage('Added to hero carousel');
      invalidateAll();
    },
    onError: (err) => setMessage((err as Error).message),
  });

  const bulkMutation = useMutation({
    mutationFn: (resource_keys: string[]) =>
      apiJson<BulkCreateResponse>('/v1/admin/featured-resources/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resource_keys }),
      }),
    onSuccess: (data) => {
      const added = data.created.length;
      const skipped = data.skipped.length;
      setMessage(
        added === 0
          ? skipped
            ? 'Already on the carousel'
            : 'Nothing added'
          : `Added ${added}${skipped ? ` (${skipped} skipped)` : ''}`
      );
      invalidateAll();
    },
    onError: (err) => setMessage((err as Error).message),
  });

  const patchMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: Record<string, unknown>;
    }) =>
      apiJson<FeaturedResource>(`/v1/admin/featured-resources/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      invalidateAll();
    },
    onError: (err) => setMessage((err as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson<{ ok: boolean }>(`/v1/admin/featured-resources/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      invalidateAll();
    },
    onError: (err) => setMessage((err as Error).message),
  });

  const reorderMutation = useMutation({
    mutationFn: (ordered_ids: number[]) =>
      apiJson<FeaturedResource[]>('/v1/admin/featured-resources/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordered_ids }),
      }),
    onSuccess: () => {
      invalidateAll();
    },
    onError: (err) => setMessage((err as Error).message),
  });

  async function uploadHeroImage(id: number, file: File) {
    setMessage(null);
    try {
      // Server-side upload avoids browser CORS / ACL failures to Spaces.
      const form = new FormData();
      form.append('file', file);
      await apiJson<FeaturedResource>(
        `/v1/admin/featured-resources/${id}/hero/upload`,
        { method: 'POST', body: form }
      );
      invalidateAll();
      setMessage('Hero image uploaded');
    } catch (err) {
      setMessage(getApiErrorMessage(err, 'Hero upload failed'));
    }
  }

  function moveRow(id: number, direction: -1 | 1) {
    const rows = listQuery.data ?? [];
    const index = rows.findIndex((row) => row.id === id);
    if (index < 0) return;
    const next = index + direction;
    if (next < 0 || next >= rows.length) return;
    const ordered = [...rows];
    const [item] = ordered.splice(index, 1);
    ordered.splice(next, 0, item);
    reorderMutation.mutate(ordered.map((row) => row.id));
  }

  function addSuggestion(item: SuggestionItem) {
    if (item.already_featured) return;
    bulkMutation.mutate([item.resource_key]);
  }

  function addAllSuggestions(items: SuggestionItem[]) {
    const keys = items
      .filter((item) => !item.already_featured)
      .map((item) => item.resource_key);
    if (keys.length === 0) {
      setMessage('Nothing new to add from this list');
      return;
    }
    bulkMutation.mutate(keys);
  }

  function displayTitle(row: FeaturedResource): string {
    if (row.title_override?.trim()) return row.title_override.trim();
    return catalogByKey.get(row.resource_key)?.title ?? row.resource_key;
  }

  if (listQuery.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (listQuery.isError) {
    return (
      <p className="text-sm text-red-600">
        {(listQuery.error as Error).message}
        <button
          type="button"
          className="ml-2 underline"
          onClick={() => void listQuery.refetch()}
        >
          Retry
        </button>
      </p>
    );
  }

  const rows = listQuery.data ?? [];
  const newItems = newSuggestionsQuery.data?.items ?? [];
  const popularItems = popularSuggestionsQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Hero Carousel</h1>
        <p className="mt-1 text-sm text-gray-600">
          Exact ordered list shown in the Learning Hub hero. The app no longer
          auto-fills from New or Popular — only what you put here (active,
          max {HERO_CAROUSEL_MAX}).
        </p>
        <p className="mt-1 text-xs text-gray-500">
          {activeCount} active · {rows.length} total
          {activeCount > HERO_CAROUSEL_MAX
            ? ` · app shows the first ${HERO_CAROUSEL_MAX} active`
            : null}
        </p>
      </div>

      {message ? <p className="text-sm text-gray-700">{message}</p> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <SuggestionCard
          title="From New"
          subtitle="Cloud content published in the last 14 days"
          loading={newSuggestionsQuery.isLoading}
          error={newSuggestionsQuery.isError}
          onRetry={() => void newSuggestionsQuery.refetch()}
          items={newItems}
          busy={bulkMutation.isPending}
          onAdd={addSuggestion}
          onAddAll={() => addAllSuggestions(newItems)}
          meta={(item) =>
            item.published_at
              ? `Published ${new Date(item.published_at).toLocaleDateString()}`
              : null
          }
        />
        <SuggestionCard
          title="From Popular"
          subtitle="Top taps by distinct installs (last 7 days)"
          loading={popularSuggestionsQuery.isLoading}
          error={popularSuggestionsQuery.isError}
          onRetry={() => void popularSuggestionsQuery.refetch()}
          items={popularItems}
          busy={bulkMutation.isPending}
          onAdd={addSuggestion}
          onAddAll={() => addAllSuggestions(popularItems)}
          meta={(item) =>
            item.distinct_installations != null
              ? `${item.distinct_installations} installs · ${item.tap_count ?? 0} taps`
              : null
          }
        />
      </div>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-gray-900">Add any resource</h2>
        <Input
          value={catalogQuery}
          onChange={(e) => setCatalogQuery(e.target.value)}
          placeholder="Search catalog by title, category, or modalLink…"
        />
        <div className="max-h-56 overflow-auto rounded-xl border border-gray-200">
          {catalogQueryResult.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : filteredCatalog.length === 0 ? (
            <p className="p-3 text-sm text-gray-500">No matching resources.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filteredCatalog.map((item) => (
                <li key={item.resource_key}>
                  <button
                    type="button"
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                      selectedKey === item.resource_key ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => setSelectedKey(item.resource_key)}
                  >
                    <div className="font-medium text-gray-900">{item.title}</div>
                    <div className="text-xs text-gray-500">
                      {item.category} · {item.source} · {item.resource_key}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[16rem] flex-1">
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Resource key (modalLink)
            </label>
            <Input
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
              placeholder="/(games)/game-snake"
            />
          </div>
          <div className="min-w-[12rem] flex-1">
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Title override (optional)
            </label>
            <Input
              value={titleOverride}
              onChange={(e) => setTitleOverride(e.target.value)}
              placeholder="Optional display title"
            />
          </div>
          <Button
            disabled={!selectedKey.trim() || createMutation.isPending}
            onClick={() =>
              createMutation.mutate({
                resource_key: selectedKey.trim(),
                title_override: titleOverride.trim() || null,
              })
            }
          >
            {createMutation.isPending ? 'Adding…' : 'Add to carousel'}
          </Button>
        </div>
      </Card>

      {rows.length === 0 ? (
        <EmptyState title="Hero carousel is empty" />
      ) : (
        <Card className="overflow-x-auto">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-sm font-medium text-gray-900">
              Carousel order
            </h2>
            <p className="text-xs text-gray-500">
              Use ↑↓ anytime — this is the exact app order
            </p>
          </div>
          <Table>
            <thead>
              <tr>
                <Th>Order</Th>
                <Th>Resource</Th>
                <Th>Hero</Th>
                <Th>Active</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <Td>
                    <div className="flex items-center gap-1">
                      <span className="w-6 text-sm text-gray-500">
                        {index + 1}
                      </span>
                      <Button
                        variant="secondary"
                        disabled={index === 0 || reorderMutation.isPending}
                        onClick={() => moveRow(row.id, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={
                          index === rows.length - 1 || reorderMutation.isPending
                        }
                        onClick={() => moveRow(row.id, 1)}
                      >
                        ↓
                      </Button>
                    </div>
                  </Td>
                  <Td>
                    <div className="font-medium text-gray-900">
                      {displayTitle(row)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {catalogByKey.get(row.resource_key)?.category ?? '—'} ·{' '}
                      {row.resource_key}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        {row.hero_image_url ? (
                          <img
                            src={row.hero_image_url}
                            alt=""
                            className="h-12 w-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-[10px] leading-tight text-gray-400">
                            Auto
                          </div>
                        )}
                        <div className="min-w-0 space-y-1">
                          {row.hero_image_cdn_key ? (
                            <Badge variant="success">Custom hero</Badge>
                          ) : (
                            <Badge variant="neutral">Default view</Badge>
                          )}
                          <div className="flex flex-wrap items-center gap-2">
                            <label className="cursor-pointer text-xs font-medium text-primary underline">
                              Upload
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) void uploadHeroImage(row.id, file);
                                  e.currentTarget.value = '';
                                }}
                              />
                            </label>
                            {row.hero_image_cdn_key ? (
                              <button
                                type="button"
                                className="text-xs text-red-600 underline"
                                onClick={() =>
                                  patchMutation.mutate({
                                    id: row.id,
                                    body: { clear_hero_image: true },
                                  })
                                }
                              >
                                Clear
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {row.hero_image_cdn_key &&
                      usesBuiltInMiniPreview(row.resource_key) ? (
                        <p className="max-w-[220px] text-[11px] leading-snug text-amber-700">
                          Uploaded, but this resource type still shows its
                          built-in mini preview in the app.
                        </p>
                      ) : !row.hero_image_cdn_key &&
                        usesBuiltInMiniPreview(row.resource_key) ? (
                        <p className="max-w-[220px] text-[11px] leading-snug text-gray-500">
                          Built-in mini preview (flashcards, games, etc.).
                        </p>
                      ) : !row.hero_image_cdn_key ? (
                        <p className="max-w-[220px] text-[11px] leading-snug text-gray-500">
                          Uses cover / local fallback art.
                        </p>
                      ) : null}
                    </div>
                  </Td>
                  <Td>
                    <button
                      type="button"
                      onClick={() =>
                        patchMutation.mutate({
                          id: row.id,
                          body: { is_active: !row.is_active },
                        })
                      }
                    >
                      <Badge variant={row.is_active ? 'success' : 'neutral'}>
                        {row.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </button>
                  </Td>
                  <Td>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove ${displayTitle(row)} from the hero carousel?`
                          )
                        ) {
                          deleteMutation.mutate(row.id);
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function SuggestionCard({
  title,
  subtitle,
  loading,
  error,
  onRetry,
  items,
  busy,
  onAdd,
  onAddAll,
  meta,
}: {
  title: string;
  subtitle: string;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  items: SuggestionItem[];
  busy: boolean;
  onAdd: (item: SuggestionItem) => void;
  onAddAll: () => void;
  meta: (item: SuggestionItem) => string | null;
}) {
  const available = items.filter((item) => !item.already_featured).length;

  return (
    <Card className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-medium text-gray-900">{title}</h2>
          <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
        </div>
        <Button
          variant="secondary"
          disabled={busy || available === 0}
          onClick={onAddAll}
        >
          Add all ({available})
        </Button>
      </div>
      <div className="max-h-64 overflow-auto rounded-xl border border-gray-200">
        {loading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : error ? (
          <p className="p-3 text-sm text-red-600">
            Failed to load.{' '}
            <button type="button" className="underline" onClick={onRetry}>
              Retry
            </button>
          </p>
        ) : items.length === 0 ? (
          <p className="p-3 text-sm text-gray-500">No suggestions right now.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((item) => {
              const detail = meta(item);
              return (
                <li
                  key={`${item.reason}:${item.resource_key}`}
                  className="flex items-center gap-2 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-900">
                      {item.title}
                    </div>
                    <div className="truncate text-xs text-gray-500">
                      {item.category}
                      {detail ? ` · ${detail}` : ''}
                    </div>
                  </div>
                  {item.already_featured ? (
                    <Badge variant="neutral">On carousel</Badge>
                  ) : (
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => onAdd(item)}
                    >
                      Add
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
